import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Collapse, Flex, Input, Table } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import $api from '../../API'
import { iconColor } from './constants'
import type { CollapseItem, SourceInfo, FileList, ItemStatus } from './types'
import { format, fromUnixTime } from 'date-fns'

export type StatusIconProps = {
  status: keyof typeof iconColor
}

function StatusIcon({ status }: StatusIconProps) {
  return <span className={iconColor[status]}>●</span>
}

function collapseKey(srcKey: string, dirIdx: number) {
  return `${srcKey}-${dirIdx}`
}

function formatDate(unixTime: number) {
  return `${format(fromUnixTime(unixTime), 'H:mm d MMM Y')}`
}

function formatSize(bytes: number) {
  const measures = 'KMGT'

  for (let i = measures.length; i > 0; --i) {
    const coef = 2 ** (i * 10)

    if (bytes >= coef) {
      return `${(bytes / coef).toFixed(1)} ${measures[i - 1]}iB`
    }
  }

  return `${bytes} bytes`
}

type PanelProps = {
  srcKey: string
  dirIdx: number
  isActive: boolean
  onStatusChange: (status: ItemStatus) => void
}

function SftpDirectoryPanel({ srcKey, dirIdx, isActive, onStatusChange }: PanelProps): JSX.Element {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filename, setFilename] = useState('')
  const [debouncedFilename, setDebouncedFilename] = useState('')

  const queryKey = useMemo(
    () => ['sftp-files', srcKey, dirIdx, page, pageSize, debouncedFilename],
    [srcKey, dirIdx, page, pageSize, debouncedFilename]
  )

  const fetchFiles = async (refresh: boolean) => {
    const params = new URLSearchParams({
      name: srcKey,
      dir: String(dirIdx),
      page: String(page),
      pageSize: String(pageSize)
    })

    if (debouncedFilename.trim().length > 0) {
      params.set('filename', debouncedFilename.trim())
    }

    if (refresh) {
      params.set('refresh', '1')
    }

    const response = await $api.get<FileList>(`/diagnostic/sftp?${params.toString()}`)
    return response.data
  }

  const listQuery = useQuery({
    queryKey,
    queryFn: () => fetchFiles(false),
    enabled: isActive
  })

  const refreshMutation = useMutation({
    mutationFn: () => fetchFiles(true),
    onMutate: () => {
      onStatusChange('pending')
    },
    onSuccess: data => {
      queryClient.setQueryData(queryKey, data)
      onStatusChange('success')
    },
    onError: () => {
      onStatusChange('failure')
    }
  })

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilename(filename)
    }, 300)

    return () => clearTimeout(handle)
  }, [filename])

  if (!isActive) {
    return <div />
  }

  if (listQuery.isError) {
    return <div className='text-red-600'>Error loading cached files.</div>
  }

  const data = listQuery.data

  return (
    <div className='flex flex-col gap-3'>
      <Flex gap='middle' className='items-center'>
        <Input
          allowClear
          placeholder='Filter by filename'
          value={filename}
          onChange={event => {
            setFilename(event.target.value)
            setPage(1)
          }}
        />
        <Button onClick={() => refreshMutation.mutate()} loading={refreshMutation.isPending}>
          Reload
        </Button>
      </Flex>

      <div className='text-gray-500'>
        Fetched At: {data?.fetchedAt ? formatDate(data.fetchedAt) : 'Not fetched yet'}
      </div>

      <Table
        columns={[
          {
            dataIndex: 'name',
            key: 'name'
          },
          {
            dataIndex: 'modifyTime',
            key: 'modifyTime',
            render: x => formatDate(x)
          },
          {
            dataIndex: 'size',
            key: 'size',
            render: x => formatSize(x)
          }
        ]}
        dataSource={data?.files ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage)
            setPageSize(nextPageSize)
          }
        }}
        bordered
        loading={listQuery.isFetching}
        rowKey='name'
        showHeader={false}
        title={undefined}
      />
    </div>
  )
}

export function SFTPDiagnostic(): JSX.Element {
  const [activeKeys, setActiveKeys] = useState<string[]>([])
  const [statusByKey, setStatusByKey] = useState<Record<string, ItemStatus>>({})
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: ['list-sftp'],
    queryFn: async () => {
      const response = await $api.get<SourceInfo[]>('/diagnostic/sftp/list')
      return response.data
    }
  })

  const refreshStatusesMutation = useMutation({
    mutationFn: async () => {
      await $api.post('/diagnostic/sftp/status/refresh')
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['list-sftp'] })
    }
  })

  useEffect(() => {
    if (!listQuery.data) {
      return
    }

    setStatusByKey(prev => {
      const next = { ...prev }
      for (const srcInfo of listQuery.data) {
        const key = collapseKey(srcInfo.key, srcInfo.dirIdx)
        next[key] = srcInfo.status
      }
      return next
    })
  }, [listQuery.data])

  const items: CollapseItem[] = useMemo(() => {
    if (!listQuery.data) {
      return []
    }

    return listQuery.data.map(srcInfo => {
      const key = collapseKey(srcInfo.key, srcInfo.dirIdx)
      const directory = srcInfo.directory?.trim() ?? ''
      const label = directory ? `${srcInfo.name} — ${directory}` : srcInfo.name
      const status = statusByKey[key] ?? 'idle'
      return {
        key,
        label: (
          <span className='flex items-center gap-2'>
            <StatusIcon status={status} />
            <span>{label}</span>
          </span>
        ),
        extra: undefined,
        children: (
          <SftpDirectoryPanel
            srcKey={srcInfo.key}
            dirIdx={srcInfo.dirIdx}
            isActive={activeKeys.includes(key)}
            onStatusChange={nextStatus =>
              setStatusByKey(prev => ({
                ...prev,
                [key]: nextStatus
              }))
            }
          />
        )
      }
    })
  }, [listQuery.data, activeKeys, statusByKey])

  return (
    <div className='flex flex-col gap-4 p-6 font-normal font-lato rounded-lg bg-lucent-background text-lucent-text'>
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl'>SFTP Diagnostic</h1>
        <Button
          onClick={() => refreshStatusesMutation.mutate()}
          loading={refreshStatusesMutation.isPending}
        >
          Refresh Statuses
        </Button>
      </div>

      {!listQuery.isLoading && (
        <Collapse
          size='large'
          items={items}
          onChange={keys => {
            const nextKeys = Array.isArray(keys) ? keys : [keys]
            setActiveKeys(nextKeys as string[])
          }}
        />
      )}
    </div>
  )
}
